import { Link, navigate } from "gatsby"
import React, { memo, useRef } from "react"
import { Region, regions } from "../../config/regions"
import { allLanguages, languages } from "../../config/languages"
import { changeLanguageAndRegion, useLocalesDispatch, useLocalesState } from "../../context/locales"
import { closeOverlay, toggleOverlay } from "../../context/locales/actions"

import FocusLock from "react-focus-lock"
import LocalesMenuStyles from "./locales.module.css"
import { OverlayType } from "../../context/locales/reducer"
import classNames from "classnames"
import { useAPIState } from "../../context/client"
import { useMutationCustomer } from "../../hooks/customer/use-query-customer"
import { useOnClickOutside } from "../../hooks/use-on-click-outside"
import { useTranslation } from "react-i18next"

export type Translations = {
    language: string
    slug: string
}[]

type LocalesMenuItemProps = {
    r: Region
    translations?: Translations
    type: OverlayType
}

const LocalesMenuItem = ({ r, translations, type }: LocalesMenuItemProps) => {
    const { region, language } = useLocalesState()

    const localesDispatch = useLocalesDispatch()

    const customer_id = useAPIState(s => s.customer_id)

    const mutation = useMutationCustomer("customerWeakToken")

    return (
        <div className={LocalesMenuStyles.languages}>
            {r.redirect
                ? r.languages
                      .filter(l => languages.some(lang => lang.langCode === l) || r.languages.length === 1)
                      .map((l, i) => (
                          <a href={r.redirect + (r.languagePaths[i] ? r.languagePaths[i] : "")} key={l} lang={l}>
                              {allLanguages.find(language => language.langCode === l)!.name}
                          </a>
                      ))
                : r.languages
                      .filter(l => languages.some(lang => lang.langCode === l))
                      .map(l => {
                          const languagePath = r.languagePaths[r.languages.findIndex(lng => lng === l)]
                              ? r.languagePaths[r.languages.findIndex(lng => lng === l)]
                                    .split("?")[0]
                                    .replace(/^\//, "")
                              : ""
                          const slug = translations?.find(t => t.language === languagePath)?.slug
                          const external =
                              process.env.NO_SITE_REGIONS.includes(r.countryCode) ||
                              (process.env.SITE_LANGUAGES ? !process.env.SITE_LANGUAGES.includes(l) : false)
                          let path = `/${languagePath}${slug ? `${languagePath ? "/" : ""}${slug}` : ""}`

                          if (external)
                              return (
                                  <a
                                      className={classNames({
                                          [LocalesMenuStyles.activeLanguage]:
                                              l === language && r.countryCode === region,
                                      })}
                                      href={path}
                                      key={languagePath}
                                      onClick={e => {
                                          e.preventDefault()
                                          changeLanguageAndRegion(localesDispatch, {
                                              language: l,
                                              region: r.countryCode,
                                          })
                                          if (customer_id) {
                                              mutation.mutate({ properties: { lang: l } })
                                          }
                                          window.location.href = path
                                          closeOverlay(localesDispatch, { type })
                                      }}
                                      lang={l}
                                  >
                                      {languages.find(language => language.langCode === l)!.name}
                                  </a>
                              )

                          if (process.env.DEFAULT_PATH) {
                              path = path.replace(process.env.DEFAULT_PATH, "")
                          }

                          return (
                              <Link
                                  className={classNames({
                                      [LocalesMenuStyles.activeLanguage]: l === language && r.countryCode === region,
                                  })}
                                  to={path}
                                  key={languagePath}
                                  onClick={e => {
                                      e.preventDefault()
                                      changeLanguageAndRegion(localesDispatch, {
                                          language: l,
                                          region: r.countryCode,
                                      })
                                      if (customer_id) {
                                          mutation.mutate({ properties: { lang: l } })
                                      }
                                      navigate(path)
                                      closeOverlay(localesDispatch, { type })
                                  }}
                                  lang={l}
                              >
                                  {languages.find(language => language.langCode === l)!.name}
                              </Link>
                          )
                      })}
        </div>
    )
}

type LocalesMenuProps = {
    translations?: Translations
    type?: OverlayType
}

const LocalesMenu = ({ translations, type = "header" }: LocalesMenuProps) => {
    const { t } = useTranslation("shared")
    const { region, language, overlayOpen, overlayType } = useLocalesState()
    const localesDispatch = useLocalesDispatch()

    const ref = useRef(null)

    useOnClickOutside(
        () => {
            if (!overlayOpen || overlayType !== type || document.getElementsByClassName("ccm-blocked")?.length) return
            closeOverlay(localesDispatch, { type })
        },
        [ref],
        [overlayOpen, type]
    )

    return (
        <div ref={ref}>
            <FocusLock
                disabled={!overlayOpen || overlayType !== type}
                returnFocus={suggestedNode => {
                    if (suggestedNode.id === `locales-menu-trigger-${type}`) return true

                    return false
                }}
            >
                <button
                    type="button"
                    onClick={() => toggleOverlay(localesDispatch, { type })}
                    className={classNames(LocalesMenuStyles.button, {
                        [LocalesMenuStyles.active]: overlayOpen && overlayType === type,
                        [LocalesMenuStyles.footerButton]: type === "footer",
                    })}
                    id={`locales-menu-trigger-${type}`}
                    aria-expanded={overlayOpen && overlayType === type}
                    aria-controls={`locales-menu-content-${type}`}
                    aria-label={t("header_locales")}
                >
                    <i className="lavita-icon"></i>
                    <div>
                        {type === "footer"
                            ? (
                                  regions.find(r => r.countryCode === region) ||
                                  regions.find(r => r.countryCode === process.env.REGION)
                              )?.name +
                              ` (${
                                  (
                                      languages.find(l => l.langCode === language) ||
                                      languages.find(l => l.langCode === process.env.DEFAULT_LANGUAGE)
                                  )?.name.toLocaleLowerCase()
                              })`
                            : (
                                  regions.find(r => r.countryCode === region) ||
                                  regions.find(r => r.countryCode === process.env.REGION)
                              )?.countryCode}
                    </div>
                </button>
                {overlayOpen && overlayType === type && (
                    <div
                        className={LocalesMenuStyles.menu}
                        id={`locales-menu-content-${type}`}
                        aria-hidden={!overlayOpen || overlayType !== type}
                        aria-labelledby={`locales-menu-trigger-${type}`}
                    >
                        <div className={LocalesMenuStyles.contentWrapper}>
                            <h3 className={LocalesMenuStyles.title} lang="en">
                                Please select your region and your language:
                            </h3>
                            <div className={LocalesMenuStyles.options}>
                                {regions
                                    .filter(r => r.flag)
                                    .map(r => (
                                        <div
                                            className={classNames(LocalesMenuStyles.flagItem, {
                                                [LocalesMenuStyles.activeItem]: r.countryCode === region,
                                            })}
                                            key={r.countryCode}
                                        >
                                            <div className={LocalesMenuStyles.flag}>{r.flag}</div>
                                            <div>
                                                <strong lang={r.language}>{r.name}</strong>
                                                <LocalesMenuItem r={r} translations={translations} type={type} />
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            <div className={LocalesMenuStyles.options}>
                                {regions
                                    .filter(r => !r.flag)
                                    .map(r => (
                                        <div
                                            className={classNames(LocalesMenuStyles.item, {
                                                [LocalesMenuStyles.activeItem]: r.countryCode === region,
                                            })}
                                            key={r.countryCode}
                                        >
                                            <strong lang={r.language}>{r.name}</strong>
                                            <LocalesMenuItem r={r} translations={translations} type={type} />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                )}
            </FocusLock>
        </div>
    )
}

export default memo(LocalesMenu)
